import {
  DynamicFormInputItems,
  DynamicFormItems,
  DynamicFormItemType,
  EnumDynamicFormNonInputItemTypes,
  IApiArgument,
  IFormInput,
  IFormInputs,
  OrUndefined,
} from "config/types";
import { sanitizeByArgumentType } from "modules/hooks/useApiArgument";

import { defaultTo, every, isEqual, keyBy, mapValues, values } from "lodash-es";

// resetValue should be object with keys=fieldId values=valueToResetTo
// eg. { firstName: "", joinNewsletter: true }
export interface IResetValues {
  [key: string]: IFormInput["value"];
}

// Reset form. Provide desired reset value for each input
export const resetFormInputs = (inputs: IFormInputs, resetValues: IResetValues): IFormInputs => {
  return mapValues(inputs, input => ({ ...input, value: resetValues[input.id], error: [] }));
};

// Build IFormInputs object from IFormInput[]
export const buildFormInputs = (inputs: Partial<IFormInput>[]): IFormInputs => {
  const formInputs: IFormInputs = {};
  const allFieldsRequired = every(inputs, ["required", true]);

  inputs.forEach(input => {
    const {
      id,
      key,
      value,
      label,
      placeholder,
      validations,
      required,
      error,
      touched,
      metadata,
      inputType,
      ...rest
    } = input;

    const lbl = label && required && !allFieldsRequired ? `${label} *` : label;

    const ph = placeholder && required && !allFieldsRequired ? `${placeholder} *` : placeholder;

    const inputId = defaultTo(id, "");
    const initialInput: IFormInput = {
      ...rest,
      id: inputId,
      key: defaultTo(key, ""),
      pristineValue: defaultTo(value, ""),
      value: defaultTo(value, ""),
      validations: defaultTo(validations, []),
      error: defaultTo(error, []),
      touched: defaultTo(touched, false),
      dirty: false,
      required: defaultTo(required, false),
      metadata: defaultTo(metadata, {}),
      inputType: defaultTo(inputType, ""),
      label: defaultTo(lbl, ""),
      placeholder: defaultTo(ph, ""),
    };

    formInputs[inputId] = initialInput;
  });

  return formInputs;
};

const buildFormInputValue = (
  inputValue: IFormInput["value"],
  inputType: DynamicFormItemType
): IFormInput["value"] => {
  try {
    const isUnitized = inputType === "inputUnitized";
    if (Array.isArray(inputValue) && isUnitized) {
      return inputValue.map(item => {
        const num = Number(item);
        return isNaN(num) ? item : num;
      });
    }

    return inputValue;
  } catch (e) {
    return inputValue;
  }
};

export const buildFormInputsFromFormItems = (items: DynamicFormItems[]) => {
  const filteredInputFormItems = items.filter(
    item => !(values(EnumDynamicFormNonInputItemTypes) as unknown[]).includes(item.type)
  ) as DynamicFormInputItems[];

  const formInputData: IFormInput[] = filteredInputFormItems.map(item => {
    const validations = [];
    const defaultValue = item.type === "inputCheckbox" ? false : "";
    const value = buildFormInputValue(defaultTo(item.content.value, defaultValue), item.type);
    let label: OrUndefined<string>;

    if (item.type === "inputText" && item.content.element === "email") {
      validations.push("email");
    }

    if (item.type === "inputDate") {
      if (item.key === "birthdate") {
        validations.push({
          name: "validateDateFormatAndRange",
          min: item.content.min,
          max: item.content.max,
        });
      } else {
        validations.push("validateDateFormat");
        validations.push({
          name: "validateDateRange",
          min: item.content.min,
          max: item.content.max,
        });
      }
    }

    if (item.type === "inputNumeric") {
      validations.push({ name: "inRange", min: item.content.min, max: item.content.max });
    }

    if (item.type === "inputTaxId") {
      if (!item.content?.disabled && !item.content?.value) {
        validations.push("ssnTin");
      }
    }

    if (item.type === "inputUnitized" && item.content.required) {
      validations.push("validateUnitizedRequired");
    }

    switch (item.type) {
      case "inputDate":
      case "inputNumeric":
      case "inputSingleSelect":
      case "inputTaxId":
      case "inputText":
        label = item.content.hint;
        break;

      default:
        label = item.content.label;
        break;
    }

    return {
      ...item.content,
      disabled: item.content.disabled,
      dirty: false,
      error: [],
      id: item.key,
      inputType: item.type,
      key: item.key,
      label,
      metadata: {
        flowQuestionId: item.content?.id,
        flowInputType: item.type,
      },
      pristineValue: value,
      required: item.content.required,
      touched: false,
      validations,
      value,
    };
  });

  return keyBy(formInputData, "id");
};

/* ------------------    FILTERS    ------------------ */
/**
 * Filters out specific button items from dynamicFormItems
 * @param items - The array of dynamic form items to filter
 * @returns The filtered array of dynamic form items
 */
export const filterPrimaryButtons = (items: DynamicFormItems[] = []): DynamicFormItems[] => {
  return items.filter(
    item =>
      item.key !== QUOTE_REVIEW_ACCEPT_PRIMARY_BUTTON_KEY &&
      item.key !== APPLY_AD_PRIMARY_BUTTON_KEY
  );
};

/* ------------------    PREDICATES    ------------------ */
export const isInputValueUnchanged = (input: IFormInput, inputArg: IApiArgument) => {
  const sanitizedInputValue = sanitizeByArgumentType(input.value, inputArg);
  return isEqual(sanitizedInputValue, input.pristineValue);
};

/**
 * Checks if a form is expired based on the expiration date
 * @param {Object} expiration - The expiration object containing effectiveUntil date
 * @return {boolean} - True if the form is expired, false otherwise
 */
export const isFormExpired = (expiration?: { effectiveUntil?: string }): boolean => {
  if (!expiration?.effectiveUntil) {
    return false;
  }
  const now = new Date();
  const expirationDate = new Date(expiration.effectiveUntil);
  return now > expirationDate;
};

/* ------------------    CONSTANTS    ------------------ */

export const QUOTE_REVIEW_ACCEPT_PRIMARY_BUTTON_KEY = "accept-offering-button";
export const APPLY_AD_PRIMARY_BUTTON_KEY = "apply-ad-button";
